/**
 * TIME — the only quantum the simulation advances in.
 *
 * PURITY LAW for everything under src/sim/: no DOM, no browser global,
 * no Math.random, no Date.now. Wall-clock time enters ONLY as an explicit
 * `nowMs` argument handed in by a caller outside the simulation.
 *
 * Every varying value in this simulation is a pure function of
 * (save seed, integer tick index). That is what makes an offline settle
 * bit-identical to a live run rather than approximately equal to one.
 */

/** One tick. The whole simulation advances in units of exactly this. */
export const TICK_MS = 1000;

/**
 * THE HOLD — how much absence is paid in full.
 *
 * TWENTY-FOUR HOURS. Owner ruling, 2026-08-31: level with the measured Melvor
 * bar, not above it. The builder had chosen 48 and the ruling reversed it, so
 * this constant is a recorded decision rather than a preference. CONTRACT.md
 * section 6 states the same number and tools/instruments/contract-check.mjs
 * fails when this file and that sentence disagree.
 *
 * Time beyond the hold is SKIPPED, never paid at a reduced rate. That choice is
 * what makes "offline pays exactly what live pays" literally true instead of
 * approximately true: there is no second, worse rate anywhere in this file, so
 * there is nothing for a reduced rate to be. A shorter hold shortens what a
 * single absence pays; it does not introduce a cheaper tick anywhere.
 *
 * The bar, MEASURED in Round 0 and recorded in gauntlet/refs/bar-depth.json:
 *   - Melvor Idle simulates offline at IDENTICAL rates to online and caps it at
 *     24 hours. Confirmed on two independent wiki pages. Melvor sells no
 *     extension to that cap at all.
 *   - Bloobs Adventure Idle banks 36h, +6h per prestige level, 96h max — and its
 *     own players at 50-1500 hours say flatly "there is no offline progress at
 *     all". Banked time is a spendable pool, not away-pays-what-present-pays.
 *     Its number is bigger and its product is worse, so its number is not a bar.
 *
 * It is ONE constant. It is not a product tier, it has no per-player value, and
 * nothing in this codebase can extend it — see entitlements.ts, whose type has
 * nowhere to put a duration. NOTHING ABOUT TIME IS EVER PURCHASABLE HERE.
 *
 * Enforcement lives in exactly one expression, in splitAbsence() in step.ts.
 * If a second clamp ever appears, instrument.test.ts fails: it counts every
 * reference to OFFLINE_HOLD_TICKS in EVERY file of this lane, this one included,
 * and asserts that the only comparison against it anywhere is that one ternary.
 * The classic failure it exists to catch is a clamp in the settle path plus a
 * leftover remainder that the next animation frame quietly pays out.
 */
export const OFFLINE_HOLD_HOURS = 24;

/** 24h at one tick per second = 86,400 ticks. */
export const OFFLINE_HOLD_TICKS = Math.round((OFFLINE_HOLD_HOURS * 3600 * 1000) / TICK_MS);

/**
 * Upper bound on a tick cursor: 2^42 ticks is roughly 139,000 years.
 * It exists so a corrupt or hostile timestamp (1e300, a clock set to the heat
 * death of the universe) produces a large SAFE INTEGER rather than a float that
 * would poison every downstream integer in the state.
 */
export const MAX_TICK = 2 ** 42;

/**
 * The largest cursor a SAVE is allowed to carry — 2^40 ticks, about 34,800
 * years of continuous play, and 1/4 of MAX_TICK.
 *
 * The headroom is the whole point. MAX_TICK is where tickAtMs saturates, and a
 * cursor sitting AT the saturation point can never advance again. Bounding a
 * loaded cursor strictly below it means the arithmetic that re-anchors a corrupt
 * save always has somewhere to put it. No honest run reaches either number:
 * both are corruption ceilings, not gameplay ones.
 */
export const MAX_SAVE_TICK = 2 ** 40;

/**
 * EVERY CEILING THIS LANE PUTS ON THE TICK CURSOR, IN ONE PLACE.
 *
 * SIM-D1, and the reason it is a registry and not two constants. The defect
 * class round 1 named for SIM-C1 was "a defensive clamp that SATURATES on a
 * value later DIFFERENCED AGAINST ITSELF". Round 2 fixed the MAX_TICK instance
 * and left the MAX_SAVE_TICK instance, because the two ceilings lived in two
 * modules and every liveness guard tested only the one the last critic named.
 *
 * MEASURED, `node src/sim/measure/run.mjs mint-window` against the round-2
 * source: a cursor written above MAX_SAVE_TICK was silently rewound by the
 * loader while its anchor was left alone, so the same gap was paid again on
 * every reload — 233,587 xp to 2,512,448 over eight tab reloads with the wall
 * clock FROZEN, and 382 of 2,000 sampled anchors (19.1%) landed in the window
 * that does it.
 *
 * The shape has no second instance now because there is no second number to
 * miss. Guards do not test a ceiling; they test THE ceiling, which is the
 * minimum of every ceiling declared here. A third ceiling added to this object
 * tightens every guard in the lane at once, and instrument.test.ts fails if a
 * `MAX_*TICK` constant is ever declared without being registered.
 */
export const CURSOR_CEILINGS: Readonly<Record<string, number>> = {
  /** Where tickAtMs saturates. Above it the target stops rising. */
  MAX_TICK,
  /** What the save normaliser clamps a loaded cursor to. Above it a WRITE is rewound. */
  MAX_SAVE_TICK,
};

/**
 * THE cursor ceiling: the tightest of them all.
 *
 * A guard that tests any single ceiling is testing a bound some other module is
 * free to undercut. Only the minimum is safe to test, because only the minimum
 * is the value at which SOME module starts rewriting the cursor.
 */
export const CURSOR_CEILING = Math.min(...Object.values(CURSOR_CEILINGS));

/**
 * The tick index that wall-clock `nowMs` falls in, for a save anchored at `epochMs`.
 *
 * PROGRESS IS COMPUTED FROM TIMESTAMPS, NEVER FROM ACCUMULATED TICKS. A tab that
 * the browser throttles to one callback a minute asks this function the same
 * question and gets the same answer as a tab running at 60fps, so throttling
 * cannot lose progress. Nothing anywhere in this simulation counts callbacks.
 *
 * Always returns a safe integer in [0, MAX_TICK].
 */
export function tickAtMs(epochMs: number, nowMs: number): number {
  if (!Number.isFinite(epochMs) || !Number.isFinite(nowMs)) return 0;
  const t = Math.floor((nowMs - epochMs) / TICK_MS);
  if (!Number.isFinite(t) || t < 0) return 0;
  if (t > MAX_TICK) return MAX_TICK;
  return t;
}

/** Absolute wall-clock ms at which tick `tick` began, for a save anchored at `epochMs`. */
export function msAtTick(epochMs: number, tick: number): number {
  return epochMs + tick * TICK_MS;
}

/**
 * THE LIVENESS QUESTION. Is there ANY future wall-clock instant at which a run
 * anchored at `epochMs` with cursor `tick` would advance by even one tick?
 *
 * WHY THIS EXISTS. tickAtMs saturates at MAX_TICK so a hostile timestamp cannot
 * poison the integer state. A saturating clamp on a value that is later
 * DIFFERENCED AGAINST ITSELF is a trap: once the cursor reaches the ceiling,
 * `target - tick` is zero forever, no error is raised, and no later patch can
 * reach a save already in that state. This function is the predicate that makes
 * the trap visible, and it is asked at the save door before any state is handed
 * to the simulation. See repairCursor() in save.ts.
 *
 * It is exact rather than heuristic: it asks tickAtMs itself, at the very
 * instant the cursor would next move, whether the answer is greater.
 */
export function canAdvanceFrom(epochMs: number, tick: number): boolean {
  if (!Number.isFinite(epochMs) || !Number.isFinite(tick)) return false;
  // THE NEXT TICK MUST BE BELOW THE CEILING, not this one. A cursor at
  // CURSOR_CEILING - 1 has nowhere legal to move to: the only index above it is
  // the ceiling itself, which the save path rewrites. Asking about `tick` alone
  // would call that state live and then refuse to advance it, and a predicate
  // that disagrees with the guard it exists to describe is the SIM-C1 shape
  // wearing a different number.
  if (tick + 1 >= CURSOR_CEILING) return false;
  const whenNext = msAtTick(epochMs, tick + 1);
  if (!Number.isFinite(whenNext)) return false;
  return tickAtMs(epochMs, whenNext) > tick;
}

/**
 * KEY NAMES THAT WOULD MAKE PLAYER-FACING STATE A CLOCK OR AN OBLIGATION.
 *
 * CONTRACT.md section 6: "No fail states, no timers, no punishment mechanics
 * near the core loop... 'no countdown timer in player-facing state' is a source
 * scan and is enforced", and, under Anti-features: "Quest or event timers of any
 * kind... login streaks or daily obligations."
 *
 * EXPORTED, AND THAT IS THE POINT (SIM-F8). This list existed as a literal
 * inside `instrument.test.ts`, and `src/voyage/no-steering.test.ts` held a
 * TRANSCRIBED COPY of the same eight strings under a test named "covers at
 * least what the sim lane bans, so one clause is not enforced in halves" — a
 * test written to fix a previous divergence of exactly this kind, comparing
 * against a copy rather than against the list. CONTRACT.md section 10 doctrine
 * 11 is the rule: a number in two places is a number that can disagree with
 * itself, and a list is no different.
 *
 * There is now one list and it is importable. `instrument.test.ts` iterates it.
 * A second lane wanting the same clause imports this rather than retyping it;
 * until it does, its copy can still drift, and that half is outside this lane's
 * ownership to change.
 *
 * It lives in time.ts because every entry is a clock or a schedule, and this is
 * the module that owns what this game is allowed to do with time.
 */
export const TIMER_SHAPED_KEYS = [
  'countdown',
  'expiresAt',
  'expiryMs',
  'deadline',
  'loginStreak',
  'dailyReset',
  'energyRefill',
  'staminaMs',
] as const;
